import { platform } from '@todesktop/client-core';

import { SAML_SSO_ANALYTICS_EVENTS } from '@/lib/analytics/events';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import {
  buildGithubAuthRedirectPath,
  buildGithubAuthRedirectUrl,
} from '@/lib/auth/github-auth-redirect';
import { clientLogger } from '@/lib/client-logger';
import { AUTH_FLOW, AUTH_MODE, MINUTE } from '@/lib/constants';
import { safeSessionStorage } from '@/lib/safe-storage';
import { STORAGE_KEYS } from '@/lib/storage-keys';

// Covers GitHub's sign-in round trip. A later page load is not credited to the attempt.
const SAML_SSO_SIGN_IN_ATTEMPT_TTL_MS = 15 * MINUTE;

type SamlSsoSignInAnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

/**
 * A "Sign in again" click from SAML SSO recovery. It lives in sessionStorage so the page GitHub
 * returns the user to can report whether the new token cleared the SSO block.
 */
export type SamlSsoSignInAttempt = {
  installationId: number;
  organizationLogin: string | null;
  startedAt: number;
  /** The click's analytics properties, repeated on the outcome event. */
  analyticsProperties: SamlSsoSignInAnalyticsProperties;
};

/**
 * Runs cubic's GitHub sign-in and returns to the current page. GitHub authorizes the new user
 * token for every organization with an active SSO session, and the auth callback stores it as
 * this browser session's token, so it replaces the SSO-blocked one.
 */
export function startSamlSsoSignIn({
  installationId,
  organizationLogin,
  analyticsProperties,
}: {
  installationId: number | null | undefined;
  organizationLogin: string | null;
  analyticsProperties: SamlSsoSignInAnalyticsProperties;
}) {
  const clickProperties = { ...analyticsProperties, authorization_target: 'cubic_sign_in' };
  trackClientEvent(
    SAML_SSO_ANALYTICS_EVENTS.AUTHORIZE_CLICKED,
    installationId?.toString(),
    clickProperties,
  );

  const { pathname, search } = window.location;
  if (platform.todesktop.isDesktopApp()) {
    // The desktop app signs in through the system browser, where the SSO session was started.
    // The app comes back to this page afterwards, but no attempt is recorded for it, so there is
    // no outcome to report.
    platform.os
      .openURL(
        buildGithubAuthRedirectUrl({
          flowType: AUTH_FLOW.DESKTOP,
          mode: AUTH_MODE.SIGN_IN,
          returnTo: `${pathname}${search}`,
        }),
      )
      .catch((error: unknown) => {
        clientLogger.error('[saml-sso] Failed to open GitHub sign-in in the browser', error);
      });
    return;
  }

  if (installationId != null) {
    const attempt: SamlSsoSignInAttempt = {
      installationId,
      organizationLogin,
      startedAt: Date.now(),
      analyticsProperties: clickProperties,
    };
    safeSessionStorage.setItem(STORAGE_KEYS.SAML_SSO_SIGN_IN_ATTEMPT, JSON.stringify(attempt));
  }

  window.location.assign(
    buildGithubAuthRedirectPath({
      flowType: AUTH_FLOW.WEB,
      mode: AUTH_MODE.SIGN_IN,
      returnTo: `${pathname}${search}`,
    }),
  );
}

/**
 * The recent attempt this page load came back from. An attempt must have started before this
 * page loaded, so the page that starts one, which is about to leave for GitHub, never reads it.
 */
export function readReturnedSamlSsoSignInAttempt(): SamlSsoSignInAttempt | null {
  const snapshot = safeSessionStorage.getItem(STORAGE_KEYS.SAML_SSO_SIGN_IN_ATTEMPT);
  if (!snapshot) {
    return null;
  }

  try {
    const attempt = JSON.parse(snapshot) as SamlSsoSignInAttempt;
    const startedBeforePageLoad = attempt.startedAt < performance.timeOrigin;
    const isRecent = Date.now() - attempt.startedAt <= SAML_SSO_SIGN_IN_ATTEMPT_TTL_MS;
    return startedBeforePageLoad && isRecent ? attempt : null;
  } catch {
    return null;
  }
}

export function clearSamlSsoSignInAttempt() {
  safeSessionStorage.removeItem(STORAGE_KEYS.SAML_SSO_SIGN_IN_ATTEMPT);
}
